// Из чего складывается неподтверждённый подотчёт: по людям, с переходом к каждому.
//
// ## Почему разбор раскрывается ЗДЕСЬ, а не открывается где-то
//
// Заказчик 21.09.2026: «нажимаем на подробно, чтоб провалиться и посмотреть, из чего
// складывается сумма, но ничего не происходит». Плитка вела на `/money/accountable` — то есть
// на экран, где он уже стоял. Нажатие не делало ничего видимого и вдобавок сбрасывало
// наложенные отборы: единственным его следом была потеря уже сделанной работы.
//
// Значит чинить надо не адрес. Плитка, ведущая на текущий экран, означает ровно одно: сводка
// и её разбор — ОДНО МЕСТО. Разбор поэтому раскрывается под плиткой, а не уводит и не
// открывает третий уровень панелей поверх второго.
//
// ## Почему по людям
//
// «Из чего складывается» у подотчёта значит «на ком висит». Не по датам и не по объектам: с
// объектом разговаривать нельзя, а с человеком можно — ему звонят и спрашивают чек. Поэтому
// каждая строка ведёт в его карточку, а вывод под полосой прямо называет, с кого начинать.
//
// ## Откуда данные
//
// Тот же запрос, что рисует левый список пульта, и тот же ключ кэша: на экране он уже есть,
// второго обращения к серверу разбор не делает. Разъехаться эти два числа при этом не могут
// по построению — они одно и то же.

import { useMemo } from 'react';

import type { СтрокаСтруктуры } from '@/shared/reports/СтруктураСуммы';
import { СтруктураСуммы } from '@/shared/reports/СтруктураСуммы';
import { countLabel } from '@/shared/text/plural';
import { Skeleton } from '@/shared/ui/screen-state';

import { usePersons } from '../queries';
import { personPath } from '../route';

/** Сколько человек показать поимённо. Остальные сворачиваются в строку «ещё N». */
const ПОИМЁННО = 6;

export function РазборПодотчёта({
  legalEntityId,
  total,
}: {
  legalEntityId: string | undefined;
  total: number;
}) {
  // `order: 'amount'` — потому что разбор отвечает на «из чего складывается», а не «кто
  // дольше». Порядок списка слева при этом не трогается: у него свой вопрос и свой ключ.
  const люди = usePersons({ legal_entity_id: legalEntityId, order: 'amount' });

  const строки = useMemo<СтрокаСтруктуры[]>(() => {
    const items = люди.data?.items ?? [];
    return items
      .filter((человек) => человек.balance.unconfirmed_balance > 0)
      .map((человек) => ({
        id: человек.id,
        title: человек.employee_name,
        href: personPath(человек.id),
        amount: человек.balance.unconfirmed_balance,
        subtitle:
          человек.oldest_unreported_days === null
            ? undefined
            : `не отчитывается ${countLabel(человек.oldest_unreported_days, ['день', 'дня', 'дней'])}`,
      }));
  }, [люди.data]);

  if (люди.isPending) {
    return <Skeleton className='h-40 w-full rounded-xl' />;
  }

  if (люди.error !== null) {
    // Разбор не имеет права выдавать свою неудачу за ответ. Плитка над ним при этом осталась
    // с верным числом: оно приехало другим запросом и от этого отказа не зависит.
    return (
      <div className='rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-4'>
        <p className='text-paragraph-sm text-text-sub-600'>
          Разбор не приехал: {люди.error.message}. Само число выше посчитано и верно — не хватает
          только списка людей, из которых оно сложилось.
        </p>
      </div>
    );
  }

  if (строки.length === 0) {
    return (
      <div className='rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-4'>
        <p className='text-paragraph-sm text-text-sub-600'>
          Складываться не из чего: неотчитанных денег сейчас нет ни на ком.
        </p>
      </div>
    );
  }

  return (
    <div className='rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-4'>
      <СтруктураСуммы
        label='Неподтверждённый подотчёт — на ком висит'
        total={total}
        // Число уже стоит крупным в плитке прямо над разбором. Второе такое же рядом человек
        // читает как ДРУГУЮ величину и ищет между ними разницу.
        showTotal={false}
        footnote={`${countLabel(строки.length, ['человек', 'человека', 'человек'])} с неотчитанными деньгами. Нажмите на фамилию, чтобы открыть карточку`}
        rows={строки}
        nameHeader='Человек'
        максимум={ПОИМЁННО}
      />
    </div>
  );
}
